/*
 *
 * Date: 2025
 * Description: List management page
 * Author: Philippe Leroux @SKITSC
 *
 */

//Modules
import { Box, Typography , CircularProgress , Modal , Button , Paper, TextField, MenuItem , Checkbox } from "@mui/material"
import { useEffect , useState , useContext, ReactElement, Fragment } from "react";
import { useNavigate } from "react-router-dom";

//Constants & functions
import { default_user, defaultCompleteList, defaultOrderPayments, defaultPayment, socketEventTypes } from "../utils/constant";
import { handleFetch } from "../api/fetch";
import { delay } from "../utils/utility";

//Interfaces
import { i_initial_props , i_modal_form_props, i_promise, i_prompt_modal_props, i_socket_response } from "../interfaces/utility.interface";
import { i_complete_list, i_list, i_makes, i_order_payments, i_payments, i_user_orders } from "../interfaces/items.interface";
import { i_list_form, i_order_filter_bar } from "../interfaces/display.interfaces";
import { t_display, t_list_status, t_method, t_socket_event, t_transition_event } from "../types/types";
import { i_user } from "../interfaces/user.interface";

//Components
import ListForm from "../components/form/list.form";
import PromptBody from "../components/modal/prompt.body";
import ListDrawer from "../components/card/list.drawer";

//Context && hooks
import { MainContext, ThemeContext } from "../context/context";
import { useAlert } from "../context/alert.context";
import { useViewModal } from "../context/view.modal.context";
import FormBody from "../components/modal/form.body";
import { useSocketEvent } from "../context/socket.context";

const Orders = ( props : i_initial_props ) : ReactElement => {
    const { HandleLogout , user } = useContext(MainContext)
    const { mode } = useContext(ThemeContext)

    //Hooks
    const { showTopAlert } = useAlert();
    const { showUserModal } = useViewModal();

    //States
    const [ display , setDisplay ] = useState<'view' | 'form' | 'submit'>('view')
    const [ subDisplay , setSubDisplay ] = useState<t_display>('form')
    const [ filter , setFilter ] = useState<i_order_filter_bar>({ status : 'Any' , admin : false})
    const [ data , setData ] = useState<i_user_orders[]>([])
    const [ selectedUser , setSelectedUser ] = useState<i_user>(default_user)
    const [ makes , setMakes ] = useState<i_makes[]>([])
    const [ form , setForm ] = useState<i_complete_list>(defaultCompleteList)
    const [ paymentForm , setPaymentForm ] = useState<i_order_payments>(defaultOrderPayments)
    const [ pErrors , setPErrors ] = useState<i_order_payments>(defaultOrderPayments)
    const [ userForm , setUserForm ] = useState<i_complete_list>(defaultCompleteList)
    const [ errors , setErrors ] = useState<i_complete_list>(defaultCompleteList)
    const [ loading , setLoading ] = useState<boolean>(false)
    const [ note , setNote ] = useState<string>('')
    const [ open , setOpen ] = useState<boolean>(false)
    const [ pathing ] = useState<string>(props.uploadPath + props.path + '/uploads/parts/')
    const [ modal_type , setModalType ] = useState<t_transition_event>('delete')

    const nav = useNavigate()
    useEffect(()=> {
        const getLists = async() => {
            setLoading(true)
            const res = await handleFetch('/listsv2', 'GET' , true , null)
            if(res.type === 'Success'){
                setData( user.type === 'admin' ? res.data : [res.data])
                if(user.type === 'user')
                setUserForm({ ...userForm, ...res.data})
            }
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
            }
            setLoading(false)
        }
        const getMakes = async() => {
            setLoading(true)
            const res = await handleFetch('/makes' , 'GET' , true , null)
            if(res.type === 'Success'){
                setMakes(res.data)
            }
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
            }
            setLoading(false)
        }
        getLists()
        getMakes()
    },[])
    useSocketEvent('payment', (output : i_socket_response) => {
        if(output.item && socketEventTypes.includes(output.type)){
            if(output.item._id === form._id){
                setForm({...form, payments : output.item.payments })
            }
            setData(prev => prev.map((userOrder: i_user_orders) => ({
                    ...userOrder,
                    list: userOrder.list.map(listItem =>
                        listItem._id === output.item._id ? { ...listItem , payments : output.item.payments }: listItem
                    ),
                }))
            );
        }
    })
    useSocketEvent('order', (output : i_socket_response) => {
        if(output.item && socketEventTypes.includes(output.type)){
            if(output.type === 'Update'){
                const updatedData = data.map((userOrder: i_user_orders) => ({
                    ...userOrder,
                    list: userOrder.list.map(listItem =>
                        listItem._id === output.item._id? {...output.item }: listItem
                    ),
                }))
                setData(updatedData)
            }else if(output.type === "Delete") {
                const updatedData = data.map((userOrder: i_user_orders) => ({
                    ...userOrder,
                    list: userOrder.list.filter(
                        (listItem) => listItem._id !== output.item._id
                    ),
                }));
                setData(updatedData);
            }
        }
    })
    useSocketEvent("order-active", (output: i_socket_response) => {
        if (output.item && socketEventTypes.includes(output.type)) {
            const updatedData = data.map((userOrder: i_user_orders) => ({
                ...userOrder,
                list: userOrder.list.map((listItem) => {
                    const updatedItem = output.item.find(
                        (o : any) => o._id === listItem._id
                    );
                    return updatedItem ? { ...listItem,  admin_selected  : updatedItem.admin_selected } : listItem;
                }),
            }));

            setData(updatedData);
        }
    });

    const handleList = (list : i_complete_list , event : 'form' | 'activate' , user : i_user ) : void => {
        setForm(list)
        setSelectedUser(user)
        if(event === 'form') {
            setDisplay(event)
        }else{
            handleListEdit('activate')
        }
        
    }
    const handleAdd = () => {
        setForm(defaultCompleteList)
        setDisplay('form')
        setErrors(defaultCompleteList)
        setNote('')
    }
    const handleListEdit = async( type : 'update' | 'delete' | 'activate') : Promise<void> => {
        setLoading(true)
        const method = type === 'delete' ? 'DELETE' : type === 'update' && form._id !== '' ? 'PUT' : type === 'update' && form._id === '' ? 'POST' : 'PATCH';
        //Some kind of validation here for evils fields...
        const arg = type === 'delete'? "/"+ form._id :  type === 'activate'? "/activate/"+ form._id : ''
        const res = await handleFetch('/list' + arg , method , true , form)
        setLoading(false)
        if(type === 'delete') setOpen(false)
        if(res.type === 'Success'){
            setDisplay('view')
            // if(type === 'update') setData(data.map((item : i_list)=> item._id === form._id ? form : item))
            // if(type === 'delete') setData(data.filter((item : i_list)=> item._id!== form._id))
        }else{
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
                return
            }
           
        } 
        showTopAlert(res)
    }
    const handleModalCallback = () : void => {
        if(modal_type === 'delete'){
            handleListEdit('delete')
        }
        if(modal_type === 'delete payment'){
            handlePayment('delete')
        }
        
    }
    const handleSelectedCB = async( value : i_list) => {
        const res = await handleFetch('/lists/activate/' + value._id + '/' + value.user_id, 'PATCH' , true , null)
        if(res.type === 'Success'){
            // setRefresh(refresh + 1)
        }
        if(res.type === 'Unauthorized'){
            HandleLogout(nav)
        }else{
            showTopAlert(res)
        }
    }
    const handleSubmit = async( ) : Promise<void> => {
        setLoading(true)
        await delay(1000)
        const res = await handleFetch('/list/order', 'POST' , true , userForm)
        if(res.type === 'Success'){
            setForm(defaultCompleteList)
            setDisplay('submit')
        } else{
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
                return
            }
            showTopAlert(res)
        }
        setLoading(false)
    }
    const handleField = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilter({ ...filter, status : event.target.value as t_list_status });
    };
    const handleBox = (event : React.ChangeEvent<HTMLInputElement>) => {
        setFilter({...filter, admin : event.target.checked });
    }
    const handleUserSelection = ( user : i_user_orders) => {
        showUserModal(user)
    }
    const handleModalPrompt = ( type : t_display) => {
        if(type === 'delete') setModalType('delete')
        if(type === 'payment'){
            setModalType('update')
            //Ton customer dans ton cas
            const monCustomer = {}
            const defaultForm = { ...default_user , ...monCustomer }
            setPaymentForm({ ...paymentForm, _id : form._id , payment : defaultPayment })
        } 
        setOpen(true)
    }
    const handlePayment = async( type : 'add' | 'delete' | 'put') => {
        //Lever here maybe to check which endpoint
        let valid : boolean = true
        if(valid){
            var method : t_method = type === 'add' ? 'POST' :  type === 'put' ? 'PUT' : 'DELETE'
            const res : i_promise = await handleFetch('/order/payment', method , true , paymentForm)
            if(res.type === 'Success'){
                setOpen(false)
            }else{
                if(res.type === 'Unauthorized'){
                    HandleLogout(nav)
                }
            }
            showTopAlert(res)
        }else{
            //Handle fields errors
        }
    }
    const handleSubCallback = ( type : t_transition_event , index : number ) => {
        if(type === 'add' || type === 'update') setModalType('update')
        if(type === 'delete') setModalType('delete payment')
        setPaymentForm({ ...paymentForm, _id : form._id , payment : type === 'add' ? defaultPayment : form.payments[index] , index : index })
        setOpen(true)
    }
    const listForm : i_list_form  = {
       form : form,
       errors : errors,
       display : subDisplay,
       setSubDisplay : setSubDisplay,
       subCallback : handleSubCallback,
       note : note,
       user : default_user,
       setNote : setNote,
       setErrors : setErrors,
       loading : loading,
       setForm : setForm,
       setDisplay : setDisplay,
       callback : handleListEdit,
       modalCallback : handleModalPrompt
    }
    
    const prompt_props : i_prompt_modal_props = {
        open : open,
        title : modal_type === "delete" ? "Order : " +form.order_number + " ?" : "Payment of " + paymentForm.payment.amount + " $ ?",
        loading : loading,
        handleClose : () => setOpen(false),
        callback : handleModalCallback,
        type : modal_type,
        mode : mode
    }
    const paymentFormBody : i_modal_form_props = {
        loading : loading,
        type : 'payment',
        callback : handlePayment,
        open : open,
        handleClose : () => setOpen(false),
        title : 'Add a new payment',
        form : paymentForm,
        select_arr : [],
        setForm: setPaymentForm,
        mode : mode,
        errors : pErrors,
    }

    const listValues : t_list_status[] = [
      'pending' , 'cancelled' , 'completed' , 'initial' , 'transit'
    ]
    return (
        <Box >
            <Box sx={{position: 'sticky', top: 0, height : '100%', overflowY: 'auto'}}>
                <>
                {loading === true ?
                    <Box sx={{ display : 'flex' , justifyContent : 'center' , marginTop : '40vh'}}>
                        <CircularProgress size={100}/>
                    </Box> 
                    :
                    <Box>
                        {display === 'view' &&
                            <Box>
                                <Box sx={{ padding : '25px'}}>
                                    <Typography variant="h4" sx={{ textAlign : 'center'}}>{ "Order management" }</Typography>
                                </Box>
                                <Paper elevation={4} sx={{maxWidth : '1400px' , display : 'flex' , marginLeft : 'auto' , marginRight : 'auto' , padding : 2 }}>
                                    <Typography variant="h6" sx={{ alignSelf : 'center' , marginRight : '12px'}}>
                                        User's order list..
                                    </Typography>
                                    <Typography sx={{ alignSelf : 'center' , marginLeft : 'auto', marginRight : '12px'}}>Show Admin's</Typography>
                                    <Checkbox checked={filter.admin} onChange={handleBox} sx={{ alignSelf : 'center' , marginRight : '12px'}}></Checkbox>
                                    <Typography sx={{ alignSelf : 'center' , marginLeft : 'auto' , marginRight : '12px'}}>
                                        Filter status :
                                    </Typography>
                                    <TextField sx={{ alignSelf : 'center' , marginRight : '12px'}} size="small" select value={filter.status} onChange={handleField}>
                                        <MenuItem value="Any">Any</MenuItem>
                                        {listValues.map(( value : t_list_status , i : number) => 
                                            <MenuItem key={i} value={value}>{value}</MenuItem>
                                        )}
                                    </TextField>
                                    <Button variant="contained" sx={{  alignSelf : 'center'}}  onClick={() => handleAdd()}>Add a new list</Button>
                                </Paper>
                                {data.map(( item : i_user_orders , i : number ) => {
                                    const shouldShowUser = item.user.type !== 'admin' || (filter.admin === true && item.user.type === 'admin');
                                    if (!shouldShowUser) return null;
                                    return (
                                        <Fragment key={i}>
                                            <Box key={i}>
                                                <Box sx={{ width : '100%' , maxWidth : '1400px' , padding : '6px' , border : '1px solid gray' , marginLeft : 'auto' , marginRight : 'auto'}}>
                                                    <Box sx={{ display : 'flex'}}>
                                                        <Typography onClick={() => handleUserSelection(item)} sx={{ textDecoration : 'underline' , fontWeight : 800 , fontSize :20 , cursor : 'pointer'}}>{item.user.username}</Typography>
                                                    </Box>
                                                </Box>
                                                {item.list.filter((list: i_complete_list) => {
                                                    const isStatusMatch = filter.status === "Any" || list.status === filter.status;
                                                    const isInitialAllowed = filter.status === "initial" || list.status !== "initial";
                                                    return isStatusMatch && isInitialAllowed;
                                                    }).map((list: i_complete_list, i: number) => (
                                                    <Box key={i}>
                                                        <ListDrawer filter={filter} list={list} user={user} callback={handleList} selectedCb={handleSelectedCB} path={pathing} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Fragment>
                                    )
                                })}
                            
                            </Box>
                        }
                        {display === 'form' && 
                            <Box>
                                <ListForm {...listForm  } />
                            </Box>
                        }
                    </Box>
                }</>
            </Box>
            <Modal open={open} onClose={() => setOpen(false)} children={ modal_type === 'update' ? <FormBody {...paymentFormBody}/>: <PromptBody {...prompt_props}/>  } />
        </Box>
    )
}

export default Orders